import PropTypes from 'prop-types';
import { m as motion } from 'framer-motion';
import { useSearch } from '@eti/providers';
import { css, etiColors } from '@eti/styles';
import { ResultFilter, ResultSetMetaData, TripRoute } from '@eti/types';
import ResultFiltersHeader from '../search-filters/v1/ResultFiltersHeader';
import SearchFilters from '../search-filters/v1/SearchFilters';
import ResultPageDebugTools from '../search-filters/debug/ResultPageDebugTools';
import { resetAllFilters } from '../search-filters/utils/searchFilters';

const filterContentStyles = css`
  padding-top: 8px;
`;

const isDebugEnabled = window.spa_variables?.debugEnabled;

const motionVariants = {
  show: {
    height: 'auto',
    transitionEnd: {
      borderBottom: `1px dotted ${etiColors.grayScale[9]}`,
      marginBottom: '30px',
      overflow: 'visible',
    },
  },
  hide: {
    overflow: 'hidden',
    height: 0,
    transitionEnd: {
      borderBottom: 0,
      marginBottom: 0,
      overflow: 'hidden',
    },
  },
};

const ResultSearchFilters = ({
  availableSortTypes,
  debugFilters,
  filteredFlightsCount,
  flightsCount,
  handleClose,
  handleToggle,
  isOpen,
  isSearchLoading,
  onSort,
  resultSetMetaData,
  searchRoutes,
}) => {
  const { currentSearchData, setSearchData } = useSearch();
  const onFilter = (filter) => {
    setSearchData({ filters: { ...currentSearchData?.filters, ...filter } });
  };
  const handleResetAll = () => {
    setSearchData({
      filters: { ...resetAllFilters(currentSearchData?.filters) },
      shouldOnlyShowDirectFlights: false,
    });
  };
  return (
    <div>
      <ResultFiltersHeader
        filteredFlightsCount={filteredFlightsCount}
        flightsCount={flightsCount}
        isOpen={isOpen}
        isSearchLoading={isSearchLoading}
        onClick={handleToggle}
        onResetAll={handleResetAll}
        selectedFilters={currentSearchData?.filters}
      />
      <motion.div
        animate={isOpen ? 'show' : 'hide'}
        className={filterContentStyles}
        data-testid="resultPage-searchFilters-content"
        hidden={!isOpen}
        initial={false}
        transition={{
          type: 'tween',
          duration: 0.3,
        }}
        variants={motionVariants}
      >
        <SearchFilters
          onClear={handleResetAll}
          onDone={handleClose}
          onFilter={onFilter}
          resultSetMetaData={resultSetMetaData}
          searchRoutes={searchRoutes}
          selectedFilters={currentSearchData?.filters}
        />
      </motion.div>
      {isDebugEnabled && (
        <ResultPageDebugTools
          availableSortTypes={availableSortTypes}
          debugFilters={debugFilters}
          filteredFlightsCount={filteredFlightsCount}
          flightsCount={flightsCount}
          onFilter={onFilter}
          onResetAll={handleResetAll}
          onSort={onSort}
          selectedFilters={currentSearchData?.filters}
          selectedSortType={currentSearchData.selectedSortType}
        />
      )}
    </div>
  );
};

ResultSearchFilters.propTypes = {
  availableSortTypes: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  debugFilters: PropTypes.arrayOf(ResultFilter),
  filteredFlightsCount: PropTypes.number,
  flightsCount: PropTypes.number,
  handleClose: PropTypes.func,
  handleToggle: PropTypes.func,
  isOpen: PropTypes.bool,
  isSearchLoading: PropTypes.bool,
  onSort: PropTypes.func,
  resultSetMetaData: ResultSetMetaData,
  searchRoutes: PropTypes.arrayOf(TripRoute),
};

export default ResultSearchFilters;
